/**
 * Invoice Background Templates
 * Decoration-free: only background colour/accent styles are defined.
 * The background is kept white for professional layouts.
 */
import type { FC, CSSProperties } from 'react';

export type TemplateId =
  | 'none'
  | 'clean' | 'ocean' | 'midnight' | 'forest'
  | 'sunset' | 'rose' | 'slate' | 'golden'
  | 'aurora' | 'carbon';

export interface InvoiceTemplate {
  id: TemplateId;
  name: string;
  description: string;
  backgroundStyle: CSSProperties;
  headingColor: string;
  labelColor: string;
  bodyColor: string;
  tableHeaderBg: string;
  tableHeaderColor: string;
  rowBorderColor: string;
  totalBg: string;
  thumbnailGradient: string;
  Decorations: FC;
}

const NoDecorations: FC = () => null;

export const INVOICE_TEMPLATES: InvoiceTemplate[] = [
  {
    id: 'none',
    name: 'Minimalist',
    description: 'Clean professional gray layout',
    backgroundStyle: { background: '#ffffff' },
    headingColor: '#1e293b', labelColor: '#64748b', bodyColor: '#0f172a',
    tableHeaderBg: '#f8fafc', tableHeaderColor: '#475569', rowBorderColor: '#e2e8f0',
    totalBg: '#f8fafc',
    thumbnailGradient: 'linear-gradient(135deg, #f1f5f9 0%, #cbd5e1 100%)',
    Decorations: NoDecorations,
  },
  {
    id: 'ocean',
    name: 'Corporate Accent',
    description: 'Structured corporate blue layout',
    backgroundStyle: { background: '#ffffff' },
    headingColor: '#1e3a8a', labelColor: '#4b5563', bodyColor: '#1e293b',
    tableHeaderBg: '#eff6ff', tableHeaderColor: '#1e40af', rowBorderColor: '#dbeafe',
    totalBg: '#f8fafc',
    thumbnailGradient: 'linear-gradient(135deg, #eff6ff 0%, #3b82f6 100%)',
    Decorations: NoDecorations,
  },
  {
    id: 'midnight',
    name: 'Modern Executive',
    description: 'Bold charcoal executive layout with dark top header',
    backgroundStyle: { background: '#ffffff' },
    headingColor: '#0f172a', labelColor: '#475569', bodyColor: '#0f172a',
    tableHeaderBg: '#1e293b', tableHeaderColor: '#ffffff', rowBorderColor: '#cbd5e1',
    totalBg: '#f1f5f9',
    thumbnailGradient: 'linear-gradient(135deg, #334155 0%, #0f172a 100%)',
    Decorations: NoDecorations,
  },
  {
    id: 'forest',
    name: 'Left Sidebar',
    description: 'Modern two-column format with vertical sidebar',
    backgroundStyle: { background: '#ffffff' },
    headingColor: '#334155', labelColor: '#64748b', bodyColor: '#0f172a',
    tableHeaderBg: '#f1f5f9', tableHeaderColor: '#334155', rowBorderColor: '#cbd5e1',
    totalBg: '#f8fafc',
    thumbnailGradient: 'linear-gradient(135deg, #e2e8f0 0%, #475569 100%)',
    Decorations: NoDecorations,
  },
  {
    id: 'rose',
    name: 'Elegant Classic',
    description: 'Serif typography and fine emerald borders',
    backgroundStyle: { background: '#ffffff' },
    headingColor: '#065f46', labelColor: '#374151', bodyColor: '#111827',
    tableHeaderBg: '#ecfdf5', tableHeaderColor: '#047857', rowBorderColor: '#a7f3d0',
    totalBg: '#f0fdf4',
    thumbnailGradient: 'linear-gradient(135deg, #d1fae5 0%, #059669 100%)',
    Decorations: NoDecorations,
  },
  {
    id: 'sunset',
    name: 'Sunset Glow',
    description: 'Warm coral and orange accents for creative freelancers',
    backgroundStyle: { background: '#ffffff' },
    headingColor: '#9a3412', labelColor: '#475569', bodyColor: '#1c1917',
    tableHeaderBg: '#fff7ed', tableHeaderColor: '#c2410c', rowBorderColor: '#ffedd5',
    totalBg: '#fff7ed',
    thumbnailGradient: 'linear-gradient(135deg, #ffedd5 0%, #ea580c 100%)',
    Decorations: NoDecorations,
  },
  {
    id: 'slate',
    name: 'Slate Professional',
    description: 'Sophisticated cool gray for tech & engineering consultants',
    backgroundStyle: { background: '#ffffff' },
    headingColor: '#0f172a', labelColor: '#64748b', bodyColor: '#1e293b',
    tableHeaderBg: '#f1f5f9', tableHeaderColor: '#334155', rowBorderColor: '#e2e8f0',
    totalBg: '#f8fafc',
    thumbnailGradient: 'linear-gradient(135deg, #f1f5f9 0%, #475569 100%)',
    Decorations: NoDecorations,
  },
  {
    id: 'aurora',
    name: 'Aurora Indigo',
    description: 'Vibrant indigo and violet style for modern agencies',
    backgroundStyle: { background: '#ffffff' },
    headingColor: '#3730a3', labelColor: '#4b5563', bodyColor: '#1e1b4b',
    tableHeaderBg: '#e0e7ff', tableHeaderColor: '#4338ca', rowBorderColor: '#c7d2fe',
    totalBg: '#e0e7ff',
    thumbnailGradient: 'linear-gradient(135deg, #e0e7ff 0%, #4f46e5 100%)',
    Decorations: NoDecorations,
  },
  {
    id: 'carbon',
    name: 'Carbon Steel',
    description: 'High contrast dark accent for trade services & contractors',
    backgroundStyle: { background: '#ffffff' },
    headingColor: '#18181b', labelColor: '#52525b', bodyColor: '#09090b',
    tableHeaderBg: '#27272a', tableHeaderColor: '#ffffff', rowBorderColor: '#e4e4e7',
    totalBg: '#f4f4f5',
    thumbnailGradient: 'linear-gradient(135deg, #3f3f46 0%, #18181b 100%)',
    Decorations: NoDecorations,
  },
];

export function getTemplate(id: TemplateId): InvoiceTemplate {
  return INVOICE_TEMPLATES.find(t => t.id === id) ?? INVOICE_TEMPLATES[0];
}
export const COLOR_PALETTE = [
  { id: 'slate', name: 'Gray', hex: '#475569', borderHex: '#e2e8f0', bgHex: '#f8fafc' },
  { id: 'blue', name: 'Blue', hex: '#1d4ed8', borderHex: '#dbeafe', bgHex: '#eff6ff' },
  { id: 'black', name: 'Black', hex: '#0f172a', borderHex: '#cbd5e1', bgHex: '#f1f5f9' },
  { id: 'red', name: 'Red', hex: '#b91c1c', borderHex: '#fee2e2', bgHex: '#fef2f2' },
  { id: 'green', name: 'Green', hex: '#047857', borderHex: '#d1fae5', bgHex: '#ecfdf5' },
  { id: 'purple', name: 'Purple', hex: '#6d28d9', borderHex: '#f3e8ff', bgHex: '#faf5ff' },
  { id: 'orange', name: 'Orange', hex: '#b45309', borderHex: '#fde68a', bgHex: '#fffbeb' },
];
